import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from "prop-types";
import Table from 'components/common/Table';
import CreatableSelect from "react-select/creatable";
import moment from "moment";
import ReactJson from "react-json-view";
import { Button } from "components/common";

// filtered events to render when page loads
const FRAUD_EVENT_PREFILTER = [
  'MANUALLY_ADDED_PAYMENT_METHOD_VERIFIED',
  'USER_ADDRESS_CHANGED',
  'USER_ADDRESS_CHANGE_REQUEST',
  'USER_DEFAULT_PAYMENT_METHOD_CHANGED',
  'USER_DOB_UPDATED',
  'USER_EMAIL_ADDRESS_UPDATED',
  'USER_NAME_UPDATED',
  'USER_PHONE_NUMBER_UPDATED',
  'USER_STATUS_CHANGED',
];

const formatEventName = (name) => name.toLowerCase().split("_").join(" ");

const prefilteredEventOptions = FRAUD_EVENT_PREFILTER.map((n) => ({ label: formatEventName(n), value: n }));

const formatSimpleAddress = (data) => {
  const {
    normalized_street: nStreet,
    normalized_city: nCity,
    normalized_postal_code: nZip,
    normalized_subdivision: nState,
  } = data || {};
  return `${nStreet}, ${nCity}, ${nState} ${nZip}`;
};

const addressChangeSnapshot = (metadata) => {
  const addressSnapshot = [];
  if (metadata.from?.billing) {
    addressSnapshot.push(`billing address updated from ${formatSimpleAddress(metadata.from.billing)}`);
  }
  if (metadata.from?.shipping) {
    addressSnapshot.push(`shipping address updated from ${formatSimpleAddress(metadata.from.shipping)}`);
  }
  return addressSnapshot;
};

const evSnapshotMapping = {
  USER_ADDRESS_CHANGED: (metadata) => addressChangeSnapshot(metadata),
  USER_ADDRESS_CHANGE_REQUEST: (metadata) => [`require review due to ${metadata.redflags}`],
  USER_DEFAULT_PAYMENT_METHOD_CHANGED: (metadata) => [`from ${metadata.old_payment_method_nickname || "N/A"} to ${metadata.new_payment_method_nickname}`],
  USER_DOB_UPDATED: (metadata) => [`from ${metadata.dobBeforeUpdate || "N/A"} to ${metadata.dobAfterUpdate}`],
  USER_EMAIL_ADDRESS_UPDATED: (metadata) => [metadata.updated_by || ""],
  USER_NAME_UPDATED: (metadata) => [`from ${metadata.nameBeforeUpdate || "N/A"} to ${metadata.nameAfterUpdate}`],
  USER_PHONE_NUMBER_UPDATED: (metadata) => [`from ${metadata.phoneNumberBeforeUpdate || "N/A"} to ${metadata.phoneNumberAfterUpdate}`],
  USER_STATUS_CHANGED: (metadata) => [`from ${metadata.from || "N/A"} to ${metadata.to}`],
};

const generateEventSnapshot = (evName, evMetadata) => {
  const mapper = evSnapshotMapping[evName];
  if (mapper && evMetadata) {
    try {
      return mapper(evMetadata);
    } catch (err) {
      return ["error generating snapshot"];
    }
  }
  return null;
};

const EventTable = ({ events = [] }) => {
  const eventNames = Array.from(new Set(events.map((ev) => ev.name)));
  const eventNameOptions = eventNames
    .sort()
    .map((n) => ({ label: formatEventName(n), value: n }));

  const [filteredEvents, setFilteredEvents] = useState(events);
  const [selected, setSelected] = useState(prefilteredEventOptions);

  const snapshotCache = {}; // storing stringified snapshots for searching purpose
  const formattedEvents = events.reduce((acc, el) => {
    const snapshot = generateEventSnapshot(el.name, el.metadata);
    if (snapshot && snapshot.length) {
      snapshotCache[el.customIdx] = snapshot.join(",");
    }

    acc[el.customIdx] = (
      <div className="w-4/5 overflow-x-auto" key={`ev-${el.customIdx}`}>
        {snapshot && snapshot.length && (<div className="mb-2">
          {snapshot.map((sn) => (<div key={`snapshot-${el.customIdx}`}>{sn}</div>))}
        </div>)}
        {el.metadata && (
          <div className="mb-2">
            <ReactJson
              src={el.metadata}
              name="details"
              theme="summerfruit:inverted"
              iconStyle="circle"
              collapsed
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
            />
          </div>
        )}
      </div>
    );
    return acc;
  }, {});

  useEffect(() => {
    if (selected.length) {
      const selectedNames = selected.map((s) => s.value);
      setFilteredEvents(events.filter((ev) => selectedNames.includes(ev.name)));
    } else {
      setFilteredEvents(events);
    }
  }, [selected, events]);

  const columns = [
    {
      Header: "Name",
      accessor: (data) => formatEventName(data.name),
    },
    {
      Header: "Details",
      accessor: (data) => snapshotCache[data.customIdx],
      Cell: (cell) => formattedEvents[cell?.row?.original.customIdx],
    },
    {
      Header: "Last Updated",
      style: {
        whiteSpace: "nowrap",
      },
      accessor: (data) =>
        (data.updated_at
          ? `${moment(data.updated_at.getTime()).format("YYYY-MM-DD LT")}`
          : "unknown"),
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-title mb-2 font-light">Events Data - {events.length}</h1>
      <div key="tag-filter" className="flex my-2">
        <span className="flex-auto mr-2">
          <CreatableSelect
            placeholder="Include event names..."
            name="supportal-event-names-filter"
            isMulti
            options={eventNameOptions}
            value={selected}
            onChange={(newSelected) => setSelected(newSelected)}
          />
        </span>

        <span>
          <Button
            onClick={() => setSelected(prefilteredEventOptions)}
          >
            <p>Reset</p>
          </Button>
        </span>
      </div>
      <div>
        <div className="my-6 justify-between w-full">
          <Table
            id="events-table"
            itemsPerPage={20}
            defaultSortOrder={{ idx: 2, order: true }}
            data={filteredEvents}
            columns={columns}
          />
        </div>
      </div>
    </div>
  );
};

EventTable.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      customIdx: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      metadata: PropTypes.object,
      created_at: PropTypes.instanceOf(Date),
      updated_at: PropTypes.instanceOf(Date),
    }),
  ).isRequired,
};

export default EventTable;
